import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../../contexts/AppContext';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import { SettingsRow, SettingsSection } from '../../configuracoes/estabelecimento/Form/SettingsGroup';
import { ServicosService } from '../../servicos/Service';
import { EstoqueService } from '../Service';
import { TriangleAlert, X } from 'lucide-react';

const estoqueService = new EstoqueService();
const servicosService = new ServicosService();

export default function EstoqueRecipes() {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { estabSession } = useEstabsContext();

    const [services, setServices] = useState<any[]>([]);
    const [items, setItems] = useState<any[]>([]);
    const [selectedServiceId, setSelectedServiceId] = useState('');
    const [recipeItems, setRecipeItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [loadingRecipe, setLoadingRecipe] = useState(false);
    const [recipeError, setRecipeError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState('');
    const [selectedQuantity, setSelectedQuantity] = useState<number | ''>(1);

    const load = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const [servicesResp, itemsResp] = await Promise.all([
                servicosService.getByEstabId(estabSession?._id).then((r) => r?.data || []),
                estoqueService.findItems({ isActive: true, limit: 200, page: 1 }).then((r) => r?.data || {}),
            ]);
            setServices(servicesResp || []);
            setItems(itemsResp?.data || []);
        } catch (error) {
            console.error(error);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (estabSession?._id) void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estabSession?._id]);

    useEffect(() => {
        let ignore = false;

        const loadRecipe = async () => {
            setRecipeItems([]);
            setRecipeError(false);
            if (!selectedServiceId) return;

            setLoadingRecipe(true);
            try {
                const recipe = await estoqueService.getRecipeByServico(selectedServiceId).then((r) => r?.data || null);
                if (!ignore) setRecipeItems(recipe?.items || []);
            } catch (error) {
                console.error(error);
                // Sem a receita atual, salvar sobrescreveria o que ja existe.
                if (!ignore) setRecipeError(true);
            } finally {
                if (!ignore) setLoadingRecipe(false);
            }
        };

        void loadRecipe();
        return () => {
            ignore = true;
        };
    }, [selectedServiceId]);

    const itemById = useMemo(
        () =>
            items.reduce((acc: any, item: any) => {
                acc[item._id] = item;
                return acc;
            }, {}),
        [items],
    );

    const availableItems = items.filter((item) => !recipeItems.some((current) => current.itemId === item._id));

    const addItem = () => {
        if (!selectedItemId || !(Number(selectedQuantity) > 0)) return;
        setRecipeItems((prev: any[]) => [...prev, { itemId: selectedItemId, quantity: Number(selectedQuantity) }]);
        setSelectedItemId('');
        setSelectedQuantity(1);
    };

    const updateQuantity = (itemId: string, quantity: number | '') => {
        setRecipeItems((prev: any[]) => prev.map((item) => (item.itemId === itemId ? { ...item, quantity } : item)));
    };

    const removeItem = (itemId: string) => {
        setRecipeItems((prev: any[]) => prev.filter((item) => item.itemId !== itemId));
    };

    const handleSave = async () => {
        if (recipeItems.some((item) => !(Number(item.quantity) > 0))) {
            errorSnackbar('Informe quantidades maiores que zero.');
            return;
        }

        setSaving(true);
        try {
            await estoqueService.upsertRecipe({
                servicoId: selectedServiceId,
                items: recipeItems.map((item) => ({ ...item, quantity: Number(item.quantity) })),
            });
            successSnackbar('Receita salva com sucesso');
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Erro ao salvar receita');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto w-full max-w-2xl space-y-3" aria-busy="true">
                <div className="sf-skeleton h-14" />
                <div className="sf-skeleton h-32" />
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar as receitas</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={load}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    return (
        <div className="estab-settings mx-auto w-full max-w-2xl space-y-6">
            <SettingsSection footer="A receita define o que sai do estoque ao concluir um atendimento deste serviço.">
                <SettingsRow label="Serviço" htmlFor="receita-servico">
                    <select
                        id="receita-servico"
                        className="select estab-field"
                        value={selectedServiceId}
                        onChange={(e) => setSelectedServiceId(e.target.value)}
                    >
                        <option value="">Selecione</option>
                        {services.map((service) => (
                            <option key={service._id} value={service._id}>
                                {service.name}
                            </option>
                        ))}
                    </select>
                </SettingsRow>
            </SettingsSection>

            {!selectedServiceId ? null : loadingRecipe ? (
                <div className="sf-skeleton h-32" aria-busy="true" />
            ) : recipeError ? (
                <div className="sf-state">
                    <TriangleAlert aria-hidden />
                    <h2>Não foi possível carregar esta receita</h2>
                    <p>Escolha o serviço de novo para tentar outra vez.</p>
                </div>
            ) : (
                <>
                    <section>
                        <h2 className="sf-group-title">Itens consumidos por atendimento</h2>
                        {recipeItems.length === 0 ? (
                            <p className="px-1 text-sm text-base-content/55">
                                Nenhum item. Sem receita, este serviço não dá baixa no estoque.
                            </p>
                        ) : (
                            <ul className="sf-group">
                                {recipeItems.map((item) => (
                                    <li key={item.itemId}>
                                        <div className="sf-row">
                                            <span className="sf-row-body">
                                                <span className="sf-row-title !font-medium">
                                                    {itemById[item.itemId]?.name || 'Item inativo ou removido'}
                                                </span>
                                            </span>
                                            <input
                                                type="number"
                                                step="any"
                                                min={0}
                                                className="input estab-field !w-20 shrink-0"
                                                value={item.quantity}
                                                aria-label={`Quantidade de ${itemById[item.itemId]?.name || 'item'}`}
                                                onChange={(e) =>
                                                    updateQuantity(item.itemId, e.target.value === '' ? '' : Number(e.target.value))
                                                }
                                            />
                                            <span className="w-8 shrink-0 text-xs text-base-content/55">
                                                {itemById[item.itemId]?.unit}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-circle shrink-0"
                                                aria-label={`Remover ${itemById[item.itemId]?.name || 'item'}`}
                                                onClick={() => removeItem(item.itemId)}
                                            >
                                                <X aria-hidden />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <SettingsSection
                        title="Adicionar item"
                        footer="Aceita frações: 0,2 significa um item a cada 5 atendimentos."
                    >
                        <SettingsRow label="Item" htmlFor="receita-item">
                            <select
                                id="receita-item"
                                className="select estab-field"
                                value={selectedItemId}
                                onChange={(e) => setSelectedItemId(e.target.value)}
                            >
                                <option value="">Selecione</option>
                                {availableItems.map((item) => (
                                    <option key={item._id} value={item._id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </SettingsRow>
                        <SettingsRow label="Quantidade" htmlFor="receita-quantidade">
                            <input
                                id="receita-quantidade"
                                type="number"
                                step="any"
                                min={0}
                                className="input estab-field"
                                value={selectedQuantity}
                                onChange={(e) => setSelectedQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                            />
                        </SettingsRow>
                        <div className="p-3">
                            <button
                                type="button"
                                className="estab-link-button"
                                disabled={!selectedItemId || !(Number(selectedQuantity) > 0)}
                                onClick={addItem}
                            >
                                Adicionar à receita
                            </button>
                        </div>
                    </SettingsSection>

                    <div className="estab-savebar">
                        <button
                            type="button"
                            className="btn btn-primary w-full sm:w-auto sm:min-w-[11rem]"
                            disabled={saving}
                            onClick={handleSave}
                        >
                            {saving && <span className="loading loading-spinner loading-sm"></span>}
                            Salvar receita
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
